import { useState } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import { 
  Send,
  Mail, 
  MapPin, 
  Clock, 
  CheckCircle,
  ExternalLink
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const Contact = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const { t } = useTranslation()
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    checkbox: '',
    website: ''
  })
  const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

// Логика отправки писем на почту
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Проверка согласия на обработку данных
    if (!acceptedPrivacyPolicy) {
      setSubmitError(t('contactPage.consent.required'))
      return
    }
    
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      //const apiBaseUrl = import.meta.env.VITE_API_URL
      const response = await fetch(`/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          checkbox: acceptedPrivacyPolicy
        }),
      })

      const data = await response.json().catch(() => null)
      if (!response.ok || !data?.ok) {
        throw new Error(data?.error || t('contactPage.error.submit'))
      }

      setIsSubmitted(true)
      setTimeout(() => setIsSubmitted(false), 5000)
    } catch (err) {
      const message = err instanceof Error ? err.message : t('contactPage.error.submit')
      setSubmitError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const services = [
    t('contactPage.services.customSoftware'),
    t('contactPage.services.ai'),
    t('contactPage.services.web'),
    t('contactPage.services.cybersecurity'),
    t('contactPage.services.cloud'),
    t('contactPage.services.consulting'),
    t('contactPage.services.architecture'),
    t('contactPage.services.support'),
    t('contactPage.services.other')
  ]

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('contactPage.title')} <span className="text-primary-600">{t('contactPage.headline')}</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('contactPage.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section ref={ref} className="section-padding bg-white">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('contactPage.form.title')}
                </h2>

                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {t('contactPage.form.success.title')}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {t('contactPage.form.success.message')}
                    </p>
                    <button 
                      onClick={() => setIsSubmitted(false)}
                      className="btn-primary"
                    >
                      {t('contactPage.form.submit.another')}
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {submitError && (
                      <p className="text-sm text-red-600">{submitError}</p>
                    )}
                    <input
                      type="text"
                      name="website"
                      value={(formData as any).website}
                      onChange={handleChange}
                      className="hidden"
                      tabIndex={-1}
                      autoComplete="off"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contactPage.form.name')}
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                          placeholder={t('contactPage.form.name.placeholder')}
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contactPage.form.email')}
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                          placeholder={t('contactPage.form.email.placeholder')}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contactPage.form.phone')}
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                          placeholder={t('contactPage.form.phone.placeholder')}
                        />
                      </div>

                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contactPage.form.company')}
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                          placeholder={t('contactPage.form.company.placeholder')}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contactPage.form.service')}
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      >
                        <option value="">{t('contactPage.form.service.select')}</option>
                        {services.map((service, index) => (
                          <option key={index} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contactPage.form.message')}
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                        placeholder={t('contactPage.form.message.placeholder')}
                      />
                    </div>

                    <label className="flex items-start gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        name="checkbox"
                        id="checkbox"
                        checked={acceptedPrivacyPolicy}
                        onChange={(e) => setAcceptedPrivacyPolicy(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        {t('contact.services.accept')}{' '}
                        <Link
                          to="/privacy-policy"
                          className="text-primary-700 hover:text-primary-800 underline font-medium"
                        >
                          {t('contact.services.accept1')}
                        </Link>
                      </span>
                    </label>
                    
                    <button
                      type="submit"
                      disabled={!acceptedPrivacyPolicy || isSubmitting}
                      className="w-full btn-primary flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? t('contactPage.form.submitting') : t('contactPage.form.submit')}
                      <Send className="w-4 h-4 ml-2" />
                    </button>
                  </form>
                )}
              </div>

            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              {/* Contact Details */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-8">
                  {t('contactPage.info.title')}
                </h2>

                <div className="space-y-6">

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{t('contactPage.email')}</h3>
                      <div className="space-y-1">
                        <a 
                          href="mailto:infotechpro.kg@gmail.com" 
                          className="block text-primary-600 hover:text-primary-700 transition-colors"
                        >
                          infotechpro.kg@gmail.com
                        </a>
                        <a 
                          href="mailto:sup.infotechpro.kg@gmail.com" 
                          className="block text-primary-600 hover:text-primary-700 transition-colors"
                        >
                          sup.infotechpro.kg@gmail.com
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{t('contactPage.address')}</h3>
                      <div className="text-gray-600 w-[250px]">
                        <div>{t('footer.address')}</div>
                      </div>
                      <a 
                        href="https://yandex.com/maps/10309/bishkek/house/Y00YcwBmQUAOQFpofXR2cntkYg==/?indoorLevel=1&ll=74.574550%2C42.874701&z=17.29" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-primary-600 hover:text-primary-700 transition-colors text-sm mt-2"
                      >
                        {t('contactPage.map.title')}
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{t('contactPage.hours')}</h3>
                      <div className="text-gray-600 space-y-1">
                        <div>{t('contactPage.hours.weekday')}</div>
                        <div>{t('contactPage.hours.saturday')}</div>
                        <div>{t('contactPage.hours.sunday')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Company Info */}
              <div className="bg-gray-50 rounded-2xl p-6 mt-6">
                <h3 className="font-semibold text-gray-900 mb-4">{t('contact.company.info')}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {t('contactPage.company.description')}
                </p>
              </div>

            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-gray-100">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('contactPage.map.title')}
            </h2>
            <p className="text-gray-600">
              {t('contactPage.map.description')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200"
          >
            <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
              <iframe
                src="https://yandex.com/map-widget/v1/-/CPWQrA2i"
                title="InfotechPRO на карте"
                className="w-full h-full"
                loading="lazy"
                allowFullScreen
              />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Contact
